import { useEffect, useMemo, useState } from 'react';

import { useGraphQLQuery } from '@betterment/js-runtime';
import { useFeatureFlag } from '@betterment/shared-components';

import { ActivityItemTypeFilterOption } from '../../graphql/schema';
import { ActivityFiltersDocument, ActivityFiltersQuery } from '../graphql/query.activityFilters.generated';
import { ACTIVITY_TYPE_LABELS } from '../utils/activityTypeLabelUtils';

type FilterOption<T> = {
  label: string;
  value: T;
};

export type DateRange = {
  startDateInclusive?: Date | null;
  endDateInclusive?: Date | null;
};

export type ActivityFilter = {
  accountId: string[];
  transactionType: ActivityItemTypeFilterOption[];
  timeframe: { label: string; dateRange: DateRange; isCustom: boolean };
};

export type AccountFilterOption = FilterOption<string> & { isClosed: boolean };
export type TransactionTypeFilterOption = FilterOption<ActivityItemTypeFilterOption>;
export type TimeframeFilterOption = FilterOption<DateRange>;

export type FilterOptions = {
  accounts: AccountFilterOption[];
  timeframe: TimeframeFilterOption[];
  transactionType: TransactionTypeFilterOption[];
};

const transformFilters: (
  isEnvelopesEnabled: boolean,
  rawFilters?: ActivityFiltersQuery
) => FilterOptions | undefined = (isEnvelopesEnabled: boolean, rawFilters?: ActivityFiltersQuery) => {
  if (rawFilters === undefined) {
    return undefined;
  }

  const { activityDateRangeOptions, activityItemTypeOptions, legalSubAccounts } = rawFilters;

  const formatAccountName = (subAccount: ActivityFiltersQuery['legalSubAccounts'][number]) => {
    const name =
      isEnvelopesEnabled && subAccount.envelope.purpose?.name
        ? `${subAccount.envelope.purpose.name} - ${subAccount.name}`
        : subAccount.name;
    return name;
  };

  const timeframe = activityDateRangeOptions?.map(option => ({
    label: option.name,
    value: {
      startDateInclusive: option.startDateInclusive,
      endDateInclusive: option.endDateInclusive,
    },
  }));

  const transactionType = activityItemTypeOptions?.map(val => ({
    label: ACTIVITY_TYPE_LABELS[val],
    value: val,
  }));

  const accounts = [...(legalSubAccounts || [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(val => ({
      label: formatAccountName(val),
      value: val.legacySubAccountId.toString(),
      isClosed: val.isClosed,
    }));

  return {
    timeframe,
    transactionType,
    accounts,
  };
};

export const useFilterOptions = (initialValues: Partial<ActivityFilter> = {}) => {
  const { enabled: isEnvelopesEnabled } = useFeatureFlag({
    splitName: 'retail.envelopes_experience_q1_2025_enabled',
    context: 'Activity',
  });

  const { data, loading, error } = useGraphQLQuery<ActivityFiltersQuery>(ActivityFiltersDocument);

  const filterOptions = useMemo(() => {
    if (data) return transformFilters(isEnvelopesEnabled, data);
    return undefined;
  }, [isEnvelopesEnabled, data]);

  const defaultFilters =
    filterOptions !== undefined
      ? {
          accountId:
            initialValues.accountId?.filter(id => filterOptions.accounts.some(account => account.value === id)) || [],
          timeframe: {
            label: filterOptions.timeframe[0].label,
            dateRange: filterOptions.timeframe[0].value,
            isCustom: false,
          },
          transactionType: [],
        }
      : undefined;

  const [filters, setFilters] = useState<ActivityFilter>();

  useEffect(() => {
    if (filterOptions !== undefined) setFilters(defaultFilters);
  }, [filterOptions]);

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const validationErrors = useMemo(() => {
    if (!filters?.timeframe.isCustom) return {};

    const { startDateInclusive, endDateInclusive } = filters?.timeframe.dateRange ?? {};
    const now = new Date();

    const isStartValid = startDateInclusive instanceof Date;
    const isEndValid = endDateInclusive instanceof Date;

    if (
      (isStartValid && startDateInclusive > now) ||
      (isEndValid && endDateInclusive > now) ||
      (isStartValid && isEndValid && startDateInclusive > endDateInclusive)
    ) {
      return {
        customTimeframe: 'One or both dates are invalid. Please check and try again.',
      };
    }

    return {};
  }, [filters?.timeframe]);

  return {
    filterOptions,
    filters,
    setFilters,
    resetFilters,
    loading,
    error,
    validationErrors,
  };
};
